import { z } from 'zod'
import { lenientNullable } from '@upadelmatch/core'

// Padel place — `places_courts/{placeId}` (docs/data-model.md). Written by the
// clubmanager projection CF (source 'clubmanager', doc id = club id) today;
// Google-bulk-import ('google') and hand-entered rows come later. Read-only
// for clients. Lenient/nullable-heavy: legacy rows predate the projection and
// must never vanish from the directory over a missing field.

export const PLACE_SOURCES = ['clubmanager', 'google', 'manual'] as const

/** Court floor enum (clubmanager's `floorSurface`) → player-facing label. */
export const SURFACE_LABELS: Record<string, string> = {
  artificialTurf: 'Césped artificial',
  concrete: 'Hormigón',
  synthetic: 'Sintética',
}

export const PadelPlaceSchema = z.object({
  id: z.string(),
  source: lenientNullable(z.enum(PLACE_SOURCES), 'padelPlace.source'),
  clubManagerId: z.string().nullable().optional(),
  name: z.string().default(''),
  address: z.string().default(''),
  city: z.string().default(''),
  province: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  geohash: z.string().nullable().optional(),
  cityKey: z.string().nullable().optional(),
  location: z
    .object({
      latitude: z.number().nullable().optional(),
      longitude: z.number().nullable().optional(),
      geohash: z.string().nullable().optional(),
      placeId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  numberOfCourts: z.number().int().nullable().optional(),
  isIndoor: z.boolean().nullable().optional(),
  isOutdoor: z.boolean().nullable().optional(),
  surfaceType: z.string().nullable().optional(),
  hasLighting: z.boolean().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  whatsappNumber: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  amenities: z.array(z.string()).default([]),
  isActive: z.boolean().default(true),

  // ── BOOKABILITY (written by clubmanager's places projection) ─────────────
  // The SLOW half of the slot grid. Present only for a club that opted into
  // «Tu club en la app»; a club that switches off has these actively CLEARED
  // by `downgrade_place_payload`, because opening hours and prices are a live
  // offer rather than identity — a lapsed club must not keep publishing a
  // price it no longer charges.
  //
  // EVERY FIELD IS OPTIONAL, and that is the contract, not laziness. The vast
  // majority of directory rows are `source: 'manual'` clubs we listed from our
  // own leads and that never connected to anything — 38 of the first 48 in
  // production. They have none of this and must keep rendering perfectly.
  /** Mirror of the club's `features.onlineBookings`. Absent ⇒ not bookable. */
  bookable: z.boolean().nullable().optional(),
  /** IANA zone of the CLUB. Every slot and dateKey anchors here, never to the
   *  device — a player in another zone must still see the club's own day. */
  timezone: z.string().nullable().optional(),
  /** `{ mon: { open: 'HH:mm', close: 'HH:mm' }, … }` in the club's timezone. */
  openingHours: z.record(z.string(), z.unknown()).nullable().optional(),
  slotDurationMinutes: z.number().int().nullable().optional(),
  /** Only courts a player could actually reserve — maintenance courts stay on
   *  the club's own grid but are never offered here. Carries `pricingRules`
   *  rather than computed prices so the shared resolver runs client-side: a
   *  price computed upstream goes stale the moment the club edits a rule. */
  bookableCourts: z
    .array(
      z.object({
        id: z.string().default(''),
        name: z.string().default(''),
        location: z.string().nullable().optional(),
        floorSurface: z.string().nullable().optional(),
        hasLighting: z.boolean().nullable().optional(),
        capacity: z.number().int().nullable().optional(),
        pricingRules: z.array(z.record(z.string(), z.unknown())).default([]),
      }),
    )
    .default([]),
  /** Days visible in the slot grid, today included (club-configurable,
   *  default 2 = Hoy/Mañana). Absent/null on any row projected before this
   *  field existed — callers fall back to 2, the fixed window every club
   *  had before it was a setting. */
  advanceBookingDays: z.number().int().nullable().optional(),
})

export type PadelPlace = z.output<typeof PadelPlaceSchema>

/** `es_granada`-style partition key for a Phase-1 (Spain) city name. */
export function cityKeyForSpain(city: string): string {
  return `es_${city.trim().toLowerCase()}`
}
